/**
 * Shared AM-visible line-hazard policy (round-38 UI / R38A-3).
 *
 * Unicode properties, not a code-point list: JSON.stringify does not
 * escape Zl/Zp/Cf, and a hand-maintained set would re-open the next
 * format character the same way C0-only did.
 *
 * `escapeAmVisibleFieldValue` JSON-quotes after this strip. Portal
 * interpolation and durable clip compose this strip so the reviewed
 * representation and the stored identity stay comparable. Do not add a
 * second escaper.
 *
 * `\p{Cf}` is stripped whole, including ZWNJ (U+200C) and ZWJ (U+200D):
 * that visibly corrupts Persian/Indic names that require those joiners.
 * That is chosen over letting two distinct stored values render
 * identically (ZWSP homograph). Do not exempt ZWNJ/ZWJ without
 * re-answering that case. R38R-5: this still does not make every
 * distinct Unicode string render distinctly (NBSP, variation selectors
 * remain).
 */

export const AM_VISIBLE_LINE_HAZARD = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;

export const AM_VISIBLE_REPLACEMENT = "\uFFFD";

/** Replace Cc/Cf/Zl/Zp with U+FFFD. Identity on ordinary punctuation and letters. */
export function replaceAmVisibleLineHazards(value: string): string {
  let cleaned = "";
  for (const char of value) {
    cleaned += AM_VISIBLE_LINE_HAZARD.test(char)
      ? AM_VISIBLE_REPLACEMENT
      : char;
  }
  return cleaned;
}

/**
 * Single-line AM-visible text. Hazards become U+FFFD, including CR/LF/TAB,
 * so a value cannot split a trusted sentence (R38O-2 / R38R-2).
 */
export function amVisibleDisplayText(value: string): string {
  return replaceAmVisibleLineHazards(value);
}

/**
 * Multi-line AM-visible prose. Intentional CR/LF stay as line breaks
 * (trusted blocker / recovery copy; #1032 newline fix). Other hazards
 * including U+2028/U+2029/bidi become U+FFFD inside each line.
 */
export function amVisibleMultilineDisplayText(value: string): string {
  return value
    .split(/\r\n|\n|\r/)
    .map(replaceAmVisibleLineHazards)
    .join("\n");
}

/**
 * True when `value` contains a line hazard other than CR/LF (those can be
 * genuine document line breaks). Used so opening-books source can show a
 * hazard note without overwriting the stored quotation or calling the
 * escaped rendering verbatim.
 */
export function amVisibleSourceNeedsHazardNote(value: string): boolean {
  for (const char of value) {
    if (char === "\n" || char === "\r") continue;
    if (AM_VISIBLE_LINE_HAZARD.test(char)) return true;
  }
  return false;
}
